// src/pages/Transfer.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import { ArrowLeft, Send, CheckCircle2, ChevronDown, Wallet } from "lucide-react";
import { useWallet } from "../context/WalletContext";

const MIN = 100;
const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

const inputClass =
  "w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 outline-none transition";

export default function Transfer() {
  const navigate = useNavigate();
  const { refreshWallet } = useWallet();
  const [balance, setBalance] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("form"); // form | confirm | done
  const [targetName, setTargetName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  useEffect(() => {
    api.get("/wallet")
      .then((res) => setBalance(Number(res.data?.balance || 0)))
      .catch(() => {});
  }, []);

  const handleContinue = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!recipient.trim()) return toast.error("Enter a username or email");
    if (!Number.isInteger(amt) || amt < MIN) return toast.error(`Minimum transfer is ${naira(MIN)}`);
    if (balance !== null && amt > balance) return toast.error("Insufficient balance");

    setBusy(true);
    try {
      const res = await api.post("/wallet/transfer/lookup", { recipient: recipient.trim() });
      setTargetName(res.data.username);
      setStep("confirm");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not find that user");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post("/wallet/transfer", {
        recipient: recipient.trim(),
        amount: Number(amount),
      });
      setBalance(Number(res.data.newBalance));
      refreshWallet(); // updates the balance in the top bar right away
      setStep("done");
      toast.success(res.data.message || "Transfer successful");
    } catch (err) {
      toast.error(err.response?.data?.message || "Transfer failed");
      setStep("form");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRecipient("");
    setAmount("");
    setTargetName("");
    setStep("form");
  };

  return (
    <div className="pb-10">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition mb-4"
        >
          <ArrowLeft size={13} /> Back
        </button>

        {/* ── Header, same style as Buy Numbers ── */}
        <div className="pt-1 pb-5 mb-5 border-b border-gray-100 dark:border-gray-800 text-center">
          <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400 mb-2">
            Transfer
          </p>
          <h1 className="font-serif text-xl sm:text-2xl font-normal text-gray-900 dark:text-white leading-snug mb-2">
            Send money to another user
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Instant · No fee · Minimum {naira(MIN)}
          </p>
        </div>

        {/* ── How to transfer — beeping green dropdown ── */}
        <div className="mb-4 rounded-xl border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowHowTo((v) => !v)}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-green-800 dark:text-green-300 animate-pulse">
              How to transfer money
            </span>
            <ChevronDown
              size={16}
              className={`shrink-0 text-green-700 dark:text-green-400 transition-transform ${showHowTo ? "rotate-180" : ""}`}
            />
          </button>

          {showHowTo && (
            <div className="px-4 pb-4 pt-1 text-sm text-green-900 dark:text-green-200 border-t border-green-200 dark:border-green-800">
              <ol className="list-decimal pl-5 space-y-1.5 leading-relaxed mt-2">
                <li>Enter the receiver's <span className="font-medium">username or email</span> and the amount (minimum ₦100).</li>
                <li>Tap <span className="font-medium">Continue</span> and check the username on the confirmation screen.</li>
                <li>Tap <span className="font-medium">Confirm &amp; Send</span>.</li>
              </ol>
              <p className="mt-3 text-xs leading-relaxed text-green-800 dark:text-green-300">
                Transfers are instant and free. The money leaves your wallet and reaches theirs immediately, and both of you will see it in your history. Transfers cannot be reversed, so double-check the username before sending.
              </p>
              <button
                type="button"
                onClick={() => setShowHowTo(false)}
                className="mt-3 text-xs font-medium text-green-700 dark:text-green-400 underline"
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* ── Main card ── */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
          {/* Balance */}
          <div className="flex items-center gap-3 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 px-4 py-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
              <Wallet size={16} className="text-purple-600 dark:text-purple-300" />
            </div>
            <div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">Available balance</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                {balance === null ? "…" : naira(balance)}
              </p>
            </div>
          </div>

          {step === "form" && (
            <form onSubmit={handleContinue} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Recipient username or email
                </label>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="username or email"
                  autoCapitalize="none"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Amount (₦)
                </label>
                <input
                  type="number"
                  min={MIN}
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Minimum ${MIN}`}
                  className={inputClass}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold text-white transition ${
                  busy ? "bg-gray-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700 active:bg-purple-800"
                }`}
              >
                {busy ? "Checking…" : "Continue"}
              </button>
            </form>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">You are sending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{naira(amount)}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                  to <span className="font-semibold text-purple-600 dark:text-purple-400">@{targetName}</span>
                </p>
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  Transfers are instant and cannot be reversed. Check the username first.
                </p>
              </div>
              <button
                onClick={handleSend}
                disabled={busy}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold text-white transition flex items-center justify-center gap-2 ${
                  busy ? "bg-gray-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700 active:bg-purple-800"
                }`}
              >
                <Send size={14} />
                {busy ? "Sending…" : "Confirm & Send"}
              </button>
              <button
                onClick={() => setStep("form")}
                disabled={busy}
                className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Edit details
              </button>
            </div>
          )}

          {step === "done" && (
            <div className="text-center py-2">
              <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={22} className="text-green-600 dark:text-green-400" />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Transfer successful</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {naira(amount)} sent to @{targetName}. It's in your transaction history.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={reset}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 active:bg-purple-800 transition"
                >
                  Send another
                </button>
                <button
                  onClick={() => navigate("/dashboard/history")}
                  className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                >
                  View history
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}